import { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle, MapPin, Globe, CreditCard, Building2, Copy, Check } from "lucide-react";
import { DarkSelect } from "@/components/ui/dark-select";
import { Button } from "@/components/ui/button";

// Canadian provinces
const CANADIAN_PROVINCES = [
  { code: "ON", name: "Ontario" },
  { code: "BC", name: "British Columbia" },
  { code: "AB", name: "Alberta" },
  { code: "SK", name: "Saskatchewan" },
  { code: "MB", name: "Manitoba" },
  { code: "QC", name: "Quebec" },
  { code: "NB", name: "New Brunswick" },
  { code: "NS", name: "Nova Scotia" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NT", name: "Northwest Territories" },
  { code: "YT", name: "Yukon" },
  { code: "NU", name: "Nunavut" }
];

export default function CheckoutModal({ 
  productType,
  productId,
  baseAmount,
  productName,
  validityDays,
  user,
  onSuccess,
  onCancel 
}) {
  const [paymentMethod, setPaymentMethod] = useState("paypal"); // "paypal" or "etransfer"
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  // Auto-populate from user registration data
  const [country, setCountry] = useState(user?.country || "CA");
  const [province, setProvince] = useState(user?.province || "");
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [priceBreakdownNoFee, setPriceBreakdownNoFee] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);
  const [etransferRef, setEtransferRef] = useState(null);
  const [etransferSent, setEtransferSent] = useState(false);

  const ETRANSFER_EMAIL = "finance@viraly.ca";

  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const response = await apiClient.get("/payments/paypal/config");
        setClientId(response.data.client_id);
      } catch (error) {
        console.error("PayPal config error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPayPalConfig();
    
    // Generate e-Transfer reference
    setEtransferRef(`VIR-${Date.now().toString(36).toUpperCase()}`);
  }, []);

  // Calculate price when location changes
  useEffect(() => {
    if (baseAmount) {
      calculatePrice();
    }
  }, [country, province, baseAmount, paymentMethod]);

  const calculatePrice = async () => {
    setCalculatingPrice(true);
    try {
      // Calculate with PayPal fee
      const paramsWithFee = new URLSearchParams({
        base_amount: baseAmount,
        country: country,
        include_gateway_fee: "true"
      });
      if (country === "CA" && province) {
        paramsWithFee.append("province", province);
      }
      
      // Calculate without fee (for e-Transfer)
      const paramsNoFee = new URLSearchParams({
        base_amount: baseAmount,
        country: country,
        include_gateway_fee: "false"
      });
      if (country === "CA" && province) {
        paramsNoFee.append("province", province);
      }
      
      const [responseWithFee, responseNoFee] = await Promise.all([
        apiClient.post(`/payments/calculate-total?${paramsWithFee}`),
        apiClient.post(`/payments/calculate-total?${paramsNoFee}`)
      ]);
      
      setPriceBreakdown(responseWithFee.data);
      setPriceBreakdownNoFee(responseNoFee.data);
    } catch (error) {
      console.error("Price calculation error:", error);
    } finally {
      setCalculatingPrice(false);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(ETRANSFER_EMAIL);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleCopyRef = () => {
    navigator.clipboard.writeText(etransferRef);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const handleEtransferConfirm = async () => {
    setProcessing(true);
    try {
      // Create pending e-Transfer payment record
      const response = await apiClient.post("/payments/etransfer/initiate", {
        product_type: productType,
        product_id: productId,
        product_name: productName,
        base_amount: baseAmount,
        total_amount: priceBreakdownNoFee?.total || baseAmount,
        tax_amount: priceBreakdownNoFee?.tax || 0,
        province: province,
        reference: etransferRef,
        validity_days: validityDays
      });
      
      if (response.data.success) {
        setEtransferSent(true);
        setSuccess(true);
        toast.success("Order confirmed! Your credits will be activated within 1 business day.");
        setTimeout(() => {
          onSuccess && onSuccess(response.data);
        }, 2000);
      }
    } catch (error) {
      console.error("E-Transfer initiation error:", error);
      toast.error("Failed to process. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const [discountCapMessage, setDiscountCapMessage] = useState(null);
  
  const createPayPalOrder = async () => {
    try {
      const response = await apiClient.post("/payments/paypal/create-order", {
        product_type: productType,
        product_id: productId,
        base_amount: baseAmount,
        country: country,
        province: province || null,
        validity_days: validityDays
      });
      
      // Store cap message if discounts were limited
      if (response.data.cap_message) {
        setDiscountCapMessage(response.data.cap_message);
      }
      
      // Update price breakdown with actual discount details
      if (response.data.pricing) {
        setPriceBreakdown({
          subtotal: response.data.pricing.subtotal,
          discount: response.data.pricing.total_discount,
          discount_details: response.data.pricing.discounts,
          tax: response.data.pricing.tax,
          gateway_fee: response.data.pricing.gateway_fee,
          total: response.data.pricing.total
        });
      }
      
      return response.data.order_id;
    } catch (error) {
      console.error("Create order error:", error);
      toast.error("Failed to create order");
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    setProcessing(true);
    try {
      const response = await apiClient.post("/payments/paypal/capture-order", {
        order_id: data.orderID,
        product_type: productType,
        product_id: productId,
        validity_days: validityDays
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success("Payment successful! Your plan is now active.");
        setTimeout(() => {
          onSuccess && onSuccess(response.data);
        }, 2000);
      }
    } catch (error) {
      console.error("Capture order error:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const currentBreakdown = paymentMethod === "etransfer" ? priceBreakdownNoFee : priceBreakdown;
  const savings = priceBreakdown && priceBreakdownNoFee 
    ? (priceBreakdown.total - priceBreakdownNoFee.total).toFixed(2)
    : null;

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
        <h3 className="text-xl font-bold text-viraly-light mb-2">Payment Successful!</h3>
        <p className="text-viraly-muted">Your {productName} is now active.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Method Tabs */}
      <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
        <button
          onClick={() => setPaymentMethod("paypal")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md transition-all ${
            paymentMethod === "paypal"
              ? "bg-viraly-orange text-white"
              : "text-viraly-muted hover:text-white"
          }`}
        >
          <CreditCard className="w-4 h-4" />
          PayPal / Card
        </button>
        {country === "CA" && (
          <button
            onClick={() => setPaymentMethod("etransfer")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md transition-all ${
              paymentMethod === "etransfer"
                ? "bg-viraly-orange text-white"
                : "text-viraly-muted hover:text-white"
            }`}
          >
            <Building2 className="w-4 h-4" />
            Interac e-Transfer
          </button>
        )}
      </div>

      {/* Savings Banner for e-Transfer */}
      {paymentMethod === "etransfer" && savings && parseFloat(savings) > 0 && (
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
          <p className="text-sm text-green-400 text-center">
            💡 Save ${savings} CAD by paying with e-Transfer!
          </p>
        </div>
      )}

      {/* Location Display - Auto-populated from registration */}
      {user?.province ? (
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4 text-viraly-orange" />
              <span className="text-viraly-muted">Tax Region:</span>
              <span className="text-viraly-light font-medium">
                {CANADIAN_PROVINCES.find(p => p.code === province)?.name || province}, {country}
              </span>
            </div>
            <span className="text-xs text-viraly-muted">(from your profile)</span>
          </div>
        </div>
      ) : (
        /* Location Selection - Only show if user hasn't set location */
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-viraly-muted">
            <Globe className="w-4 h-4" />
            Your Location (for tax calculation)
            {user?.country && <span className="text-xs text-viraly-muted ml-2">(from registration)</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DarkSelect
              value={country}
              onChange={(e) => {
                // Only allow change if not pre-set from registration
                if (!user?.country) {
                  setCountry(e.target.value);
                  if (e.target.value !== "CA") {
                    setProvince("");
                    setPaymentMethod("paypal"); // e-Transfer only for Canada
                  }
                }
              }}
              disabled={!!user?.country}
              options={[
                { value: "CA", label: "Canada" },
                { value: "US", label: "United States" },
                { value: "OTHER", label: "Other" }
              ]}
            />
            {country === "CA" && (
              <DarkSelect
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                options={[
                  { value: "", label: "Select Province" },
                  ...CANADIAN_PROVINCES.map(p => ({ value: p.code, label: p.name }))
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-viraly-muted">{productName}</span>
          <span className="text-viraly-light">${baseAmount} CAD</span>
        </div>
        
        {/* Discount Details */}
        {currentBreakdown?.discount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-green-400">Savings Applied</span>
            <span className="text-green-400">-${currentBreakdown.discount.toFixed(2)} CAD</span>
          </div>
        )}
        
        {currentBreakdown?.tax > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-viraly-muted">
              {currentBreakdown.tax_label || "Tax"}
            </span>
            <span className="text-viraly-light">${currentBreakdown.tax.toFixed(2)} CAD</span>
          </div>
        )}
        {paymentMethod === "paypal" && currentBreakdown?.gateway_fee > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-viraly-muted">Payment Processing Fee</span>
            <span className="text-viraly-light">${currentBreakdown.gateway_fee.toFixed(2)} CAD</span>
          </div>
        )}
        <div className="border-t border-white/10 pt-2 mt-2">
          <div className="flex justify-between font-bold">
            <span className="text-viraly-light">Total</span>
            <span className="text-viraly-orange">
              {calculatingPrice ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                `$${currentBreakdown?.total?.toFixed(2) || baseAmount} CAD`
              )}
            </span>
          </div>
          {/* Show local currency equivalent for non-Canadian users */}
          {currentBreakdown?.local_currency && paymentMethod === "paypal" && (
            <div className="mt-2 p-2 rounded bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-300">
                PayPal will charge approximately: <span className="font-bold">{currentBreakdown.local_currency.formatted}</span>
              </p>
              <p className="text-[10px] text-blue-300/70 mt-1">
                Final amount may vary slightly based on PayPal's exchange rate at time of payment
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Discount Cap Message - Polite explanation when discount was limited */}
      {discountCapMessage && (
        <div className="p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-300">{discountCapMessage}</p>
          </div>
        </div>
      )}

      {/* PayPal Payment */}
      {paymentMethod === "paypal" && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
            </div>
          ) : clientId ? (
            <PayPalScriptProvider options={{ 
              "client-id": clientId,
              currency: "CAD",
              intent: "capture"
            }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                disabled={processing || !province && country === "CA"}
                createOrder={createPayPalOrder}
                onApprove={handlePayPalApprove}
                onCancel={() => toast.info("Payment cancelled")}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  toast.error("Payment error. Please try again.");
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <p className="text-center text-red-400">Payment system unavailable</p>
          )}
          {country === "CA" && !province && (
            <p className="text-xs text-yellow-400 text-center">Please select your province for accurate tax calculation</p>
          )}
        </div>
      )}

      {/* E-Transfer Payment */}
      {paymentMethod === "etransfer" && (
        <div className="space-y-4">
          {!etransferSent ? (
            <>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                <h4 className="font-medium text-viraly-light">Send Interac e-Transfer to:</h4>
                
                <div className="flex items-center justify-between p-3 rounded bg-white/5">
                  <div>
                    <p className="text-xs text-viraly-muted">Email</p>
                    <p className="font-mono text-viraly-light">{ETRANSFER_EMAIL}</p>
                  </div>
                  <button
                    onClick={handleCopyEmail}
                    className="p-2 rounded hover:bg-white/10 transition-colors"
                  >
                    {copiedEmail ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-viraly-muted" />}
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded bg-white/5">
                  <div>
                    <p className="text-xs text-viraly-muted">Amount</p>
                    <p className="font-mono text-viraly-orange text-lg">
                      ${currentBreakdown?.total?.toFixed(2) || baseAmount} CAD
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded bg-white/5">
                  <div>
                    <p className="text-xs text-viraly-muted">Message/Memo (include this)</p>
                    <p className="font-mono text-viraly-light">{etransferRef}</p>
                  </div>
                  <button
                    onClick={handleCopyRef}
                    className="p-2 rounded hover:bg-white/10 transition-colors"
                  >
                    {copiedRef ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-viraly-muted" />}
                  </button>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-400">
                  <strong>Auto-deposit enabled:</strong> Your payment will be deposited automatically - no security question needed.
                </p>
              </div>
              
              {country === "CA" && !province && (
                <p className="text-xs text-yellow-400 text-center">Please select your province above for accurate tax calculation</p>
              )}
              
              <Button
                onClick={handleEtransferConfirm}
                disabled={processing || (!province && country === "CA")}
                className="w-full btn-primary"
                data-testid="etransfer-confirm-btn"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Confirm & Complete Order
              </Button>
              
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400 text-center">
                  <strong>⏱️ Activation Time:</strong> Your credits will be activated within <strong>1 business day</strong> after we confirm your e-Transfer. You'll receive an email confirmation once activated.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-viraly-light font-medium">Order Confirmed!</p>
              <p className="text-sm text-viraly-muted mt-1">Your credits will be activated within 1 business day.</p>
              <p className="text-xs text-viraly-muted mt-2">You'll receive an email once your credits are ready.</p>
            </div>
          )}
        </div>
      )}

      {/* Cancel Button */}
      {!success && (
        <button
          onClick={onCancel}
          className="w-full text-center text-sm text-viraly-muted hover:text-white transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
